import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {CreateCustomFieldDropdownContainer} from '../../../../../../../../../../src/components/common/dropdowns/customFields/createCustomFieldDropdownContainer';
import {CustomFieldDropdown} from '../../../../../../../../../../src/components/common/dropdowns/customFields/customFieldDropdown';
import {MappingRowField} from '../../../../../../../../../../src/components/common/mapping/mappingRowField';
import {computeCustomFieldIconName} from '../../../../../../../../../../src/helpers/customFieldViewHelpers';
import {
  CustomFieldModel,
  CustomFieldResourceTypesEnum,
} from '../../../../../../../../../../src/models/api/customFieldModel';
import {Handler, HandlerOf} from '../../../../../../../../../shared/helpers/src/react/reactHelpers';
import {InlineLinkButton} from '../../../../../../../../../shared/ui/src/components/buttons/inlineLinkButton';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../../../../shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../../../../../shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../../../../shared/ui/src/theme/userThemeConsumer';

/*
 * Props.
 */

interface CustomFieldDropdownWrapperProps {
  customFields: ReadonlyArray<CustomFieldModel>;
  selectedCustomField?: CustomFieldModel;
  defaultCustomFieldName?: string;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  isCustomFieldLimitReached: boolean;
  isFrontCustomFieldMissing?: boolean;
  isUserGlobalAdmin: boolean;
  allSelectedCustomFieldIds: ReadonlyArray<number>;
  onNavigateToLink: Handler;
  onSelect: HandlerOf<number>;
}

/*
 * Style.
 */

const StyledMappingPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledTooltipDiv = styled.div`
  width: 230px;
  line-height: 18px;
  font-weight: ${fontWeights.medium};
`;

const StyledInlineLinkButton = styled(InlineLinkButton)`
  color: ${(p) => p.theme.greys.white};
  font-weight: ${fontWeights.bold};
`;

/*
 * Component.
 */

export const CustomFieldDropdownWrapper: FC<CustomFieldDropdownWrapperProps> = (props) => {
  const {
    selectedCustomField,
    defaultCustomFieldName,
    isReadOnly,
    isDisabled,
    isCustomFieldLimitReached,
    allSelectedCustomFieldIds,
    isFrontCustomFieldMissing,
    isUserGlobalAdmin,
    customFields,
    onSelect,
    onNavigateToLink,
  } = props;

  const hasNoCustomFieldsToShow = customFields.length === 0;
  const [isShowingCreateDropdown, setIsShowingCreateDropdown] = useState(false);
  const [fieldName, setFieldName] = useState(defaultCustomFieldName);

  const onCancelClick = (popoverProps: PopoverTransferProps) => {
    if (hasNoCustomFieldsToShow) {
      popoverProps.onRequestClose();
    } else {
      setIsShowingCreateDropdown(false);
    }
  };

  const onCreateClick = (input?: string) => {
    if (input) {
      setFieldName(input);
    } else {
      setFieldName(defaultCustomFieldName);
    }

    setIsShowingCreateDropdown(true);
  };

  const iconName =
    selectedCustomField?.type && computeCustomFieldIconName(selectedCustomField.type, VisualSizesEnum.SMALL);

  return (
    <PopoverCoordinator
      isDisabled={isReadOnly || isDisabled}
      renderButton={() => (
        <MappingRowField
          iconName={iconName}
          isReadOnly={isReadOnly}
          isDisabled={isDisabled}
          hasError={isFrontCustomFieldMissing}
        >
          {selectedCustomField?.name ?? renderPlaceholder()}
        </MappingRowField>
      )}
      renderModal={(popoverProps) => {
        // Only global admins are allowed to create custom fields.
        if (isUserGlobalAdmin && (hasNoCustomFieldsToShow || isShowingCreateDropdown)) {
          return (
            <CreateCustomFieldDropdownContainer
              popoverProps={popoverProps}
              fieldName={fieldName}
              customFieldResourceType={CustomFieldResourceTypesEnum.ACCOUNT}
              isShowingBackButton={!hasNoCustomFieldsToShow}
              onCancelClick={() => onCancelClick(popoverProps)}
              renderDropdownTitle={renderDropdownTitle}
              onSelect={(customFieldId) => {
                onSelect(customFieldId);
                setIsShowingCreateDropdown(false);
                popoverProps.onRequestClose();
              }}
            />
          );
        }
        return (
          <CustomFieldDropdown
            customFieldResourceType={CustomFieldResourceTypesEnum.ACCOUNT}
            isCreateDisabled={isCustomFieldLimitReached}
            renderCreateDisabledTooltip={() => renderLimitReachedTooltip(onNavigateToLink)}
            popoverProps={popoverProps}
            selectedCustomFieldId={selectedCustomField?.id}
            allSelectedCustomFieldIds={allSelectedCustomFieldIds}
            onSelect={onSelect}
            customFields={customFields}
            onCreateClick={isUserGlobalAdmin ? onCreateClick : undefined}
          />
        );
      }}
    />
  );
};

/*
 * Helpers.
 */

function renderPlaceholder() {
  return (
    <StyledMappingPlaceholderDiv>
      <Trans id="accounts-csv-import-custom-field-placeholder">Add custom field</Trans>
    </StyledMappingPlaceholderDiv>
  );
}

function renderDropdownTitle() {
  return <Trans id="accounts-csv-import-create-custom-field-dropdown-title">New account custom field</Trans>;
}

function renderLimitReachedTooltip(onNavigateToLink: Handler) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledTooltipDiv>
          <Trans id="custom-field-dropdown-limit-reached-tooltip">
            You cannot create more custom fields because you have reached the maximum limit.
          </Trans>
          <div>
            <StyledInlineLinkButton
              hasTextDecoration
              hoverColor={theme.greys.white}
              onClick={onNavigateToLink}
            >
              Manage custom fields
            </StyledInlineLinkButton>
          </div>
        </StyledTooltipDiv>
      )}
    </UserThemeConsumer>
  );
}
